import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import SignaturePad from './SignaturePad';
import {
  PageHeader, DataCard, StatCard, StatusChip, ActionButton, EmptyState,
  FilterTabs, SearchInput, InfoBanner, Icons, BgArt, Avatar,
} from './customer/ui';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS = {
  pending_grantor_signature: { label: 'Ihre Unterschrift fehlt',     tone: 'pending', short: 'Ausstehend' },
  pending_grantee_signature: { label: 'Wartet auf Bevollmächtigten', tone: 'pending', short: 'Ausstehend' },
  pending_admin_review:      { label: 'Prüfung ausstehend',          tone: 'review',  short: 'Prüfung' },
  active:                    { label: 'Aktiv',                        tone: 'active',  short: 'Aktiv' },
  rejected:                  { label: 'Abgelehnt',                    tone: 'rejected',short: 'Abgelehnt' },
  revoked:                   { label: 'Widerrufen',                   tone: 'revoked', short: 'Widerrufen' },
};

const fmtDate = (d) => (d ? format(new Date(d), 'dd.MM.yyyy', { locale: de }) : '—');
const fmtDateTime = (d) => (d ? format(new Date(d), 'dd.MM.yyyy · HH:mm', { locale: de }) : '—');

const CustomerAuthorizations = ({ currentUser }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [signingAuth, setSigningAuth] = useState(null);
  const [detail, setDetail] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => { loadItems(); }, []);

  const loadItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/account-authorizations`);
      setItems(res.data);
    } catch (e) {
      toast.error('Fehler beim Laden der Vollmachten');
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (authId) => {
    if (!window.confirm('Vollmacht wirklich widerrufen? Der Zugriff wird sofort entzogen.')) return;
    try {
      await axios.delete(`${API}/account-authorizations/${authId}`);
      toast.success('Vollmacht widerrufen');
      loadItems();
      setDetail(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Widerruf');
    }
  };

  const handleSign = async (authId, signatureB64) => {
    try {
      await axios.post(`${API}/account-authorizations/${authId}/sign`, { signature_b64: signatureB64 });
      toast.success('Unterschrift erfasst');
      setSigningAuth(null);
      loadItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Unterschreiben');
    }
  };

  // === Filter + Search =======================================================
  const counts = useMemo(() => {
    const c = { all: items.length, active: 0, pending: 0, rejected: 0, revoked: 0 };
    items.forEach((a) => {
      if (a.status === 'active') c.active++;
      else if (a.status === 'rejected') c.rejected++;
      else if (a.status === 'revoked') c.revoked++;
      else c.pending++;
    });
    return c;
  }, [items]);

  const filtered = useMemo(() => {
    let list = items;
    if (filter !== 'all') {
      if (filter === 'pending') list = list.filter((a) => a.status.startsWith('pending'));
      else list = list.filter((a) => a.status === filter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((a) => {
        const isGrantor = a.role_for_me === 'grantor';
        const name = (isGrantor ? a.grantee_full_name : a.grantor_full_name) || '';
        const email = (isGrantor ? a.grantee_email : a.grantor_email) || '';
        return name.toLowerCase().includes(q) || email.toLowerCase().includes(q);
      });
    }
    return list;
  }, [items, filter, search]);

  return (
    <div className="space-y-6 lg:space-y-8" data-testid="customer-authorizations">
      <PageHeader
        title="Kontovollmachten"
        subtitle="Erteilen Sie einer anderen Person ausschließlich lesenden Zugriff auf Ihre Investments. Die Person muss bereits einen ValuePlus-Account besitzen und wird beidseitig digital verifiziert."
        action={
          <ActionButton variant="primary" size="md" icon={Icons.plus} onClick={() => setShowCreate(true)} data-testid="new-authorization-button">
            Neue Vollmacht
          </ActionButton>
        }
        testid="vollmachten-page-header"
      />

      {/* Security/info banner */}
      <InfoBanner
        tone="gold"
        icon={Icons.shield}
        title="Bank-Grade Sicherheit"
        description="Bevollmächtigte Personen erhalten ausschließlich Lesezugriff – keine Auszahlungen, keine Daten- oder Bankänderungen. Jede Vollmacht wird beidseitig digital unterschrieben und durch Value Plus geprüft. Widerruf jederzeit mit einem Klick."
        testid="auth-info-banner"
      />

      {/* KPI strip */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard
          label="Aktiv" value={counts.active} sub="Laufend"
          variant="success" icon={Icons.check} bgIcon={BgArt.shield} testid="auth-kpi-active"
        />
        <StatCard
          label="Ausstehend" value={counts.pending} sub="Unterschrift offen"
          variant="default" icon={Icons.clock} bgIcon={BgArt.hourglass} testid="auth-kpi-pending"
        />
        <StatCard
          label="Abgelehnt" value={counts.rejected} sub="Vom Admin abgelehnt"
          variant="danger" icon={Icons.x} bgIcon={BgArt.stamp} testid="auth-kpi-rejected"
        />
        <StatCard
          label="Widerrufen" value={counts.revoked} sub="Endgültig beendet"
          variant="neutral" icon={Icons.x} bgIcon={BgArt.ban} testid="auth-kpi-revoked"
        />
      </section>

      {/* Filter & search */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
        <FilterTabs
          active={filter}
          onChange={setFilter}
          tabs={[
            { id: 'all',      label: 'Alle',        count: counts.all },
            { id: 'active',   label: 'Aktiv',       count: counts.active },
            { id: 'pending',  label: 'Ausstehend',  count: counts.pending },
            { id: 'rejected', label: 'Abgelehnt',   count: counts.rejected },
            { id: 'revoked',  label: 'Widerrufen',  count: counts.revoked },
          ]}
          testid="auth-filter-tabs"
        />
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Nach Name oder E-Mail suchen…"
          className="sm:w-72"
          testid="auth-search"
        />
      </div>

      {/* List */}
      {loading ? (
        <DataCard><div className="py-10 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C5A059] mx-auto"></div></div></DataCard>
      ) : filtered.length === 0 ? (
        items.length === 0 ? (
          <EmptyState
            icon={Icons.shield}
            title="Noch keine Vollmachten"
            description="Erteilen Sie einer vertrauten Person Lesezugriff auf Ihr Konto. Beide Parteien unterschreiben digital und Value Plus prüft die Vollmacht."
            action={
              <ActionButton variant="primary" size="md" icon={Icons.plus} onClick={() => setShowCreate(true)} data-testid="empty-create-button">
                Neue Vollmacht erstellen
              </ActionButton>
            }
            testid="auth-empty-state"
          />
        ) : (
          <EmptyState
            icon={Icons.eye}
            title="Keine Treffer"
            description="Mit Ihrem aktuellen Filter / Suchbegriff wurden keine Vollmachten gefunden."
            action={
              <ActionButton variant="secondary" size="sm" onClick={() => { setFilter('all'); setSearch(''); }}>
                Filter zurücksetzen
              </ActionButton>
            }
          />
        )
      ) : (
        <div className="grid gap-3">
          {filtered.map((a) => (
            <AuthorizationCard
              key={a.id}
              auth={a}
              onClick={() => setDetail(a)}
              onSign={() => setSigningAuth(a)}
              onRevoke={() => handleRevoke(a.id)}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {showCreate && (
        <CreateAuthorizationModal
          currentUser={currentUser}
          onClose={() => setShowCreate(false)}
          onCreated={(a) => { setShowCreate(false); loadItems(); setSigningAuth(a); }}
        />
      )}
      {detail && (
        <AuthorizationDetailModal
          auth={detail}
          onClose={() => setDetail(null)}
          onRevoke={() => handleRevoke(detail.id)}
          onSign={() => { setSigningAuth(detail); setDetail(null); }}
        />
      )}
      {signingAuth && (
        <SignModal
          auth={signingAuth}
          onClose={() => setSigningAuth(null)}
          onSign={(sig) => handleSign(signingAuth.id, sig)}
        />
      )}
    </div>
  );
};

// ============================================================
// Authorization Card
// ============================================================
const AuthorizationCard = ({ auth, onClick, onSign, onRevoke }) => {
  const isGrantor = auth.role_for_me === 'grantor';
  const otherName = (isGrantor ? auth.grantee_full_name : auth.grantor_full_name) || 'Unbekannt';
  const otherEmail = isGrantor ? auth.grantee_email : auth.grantor_email;
  const status = STATUS[auth.status] || STATUS.revoked;
  const needsMySig =
    (isGrantor && auth.status === 'pending_grantor_signature') ||
    (!isGrantor && auth.status === 'pending_grantee_signature');

  return (
    <article
      className="group relative rounded-2xl bg-gradient-to-br from-white via-white to-[#FAF6EA]/40 ring-1 ring-gray-100/80 hover:ring-[#C5A059]/40 hover:shadow-[0_24px_60px_-20px_rgba(197,160,89,0.25)] hover:-translate-y-0.5 transition-all duration-500 cursor-pointer overflow-hidden"
      onClick={onClick}
      data-testid={`authorization-${auth.id}`}
    >
      {/* Soft radial accent */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(circle at 100% 100%, rgba(197,160,89,0.07) 0%, transparent 50%)' }} />
      {/* Hover bloom */}
      <div className="absolute -bottom-12 -right-12 w-32 h-32 rounded-full bg-[#C5A059]/15 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />

      <div className="relative p-4 sm:p-5 flex items-center gap-4">
        <Avatar name={otherName} size={52} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-heading text-base sm:text-lg text-[#0A0A0A] truncate tracking-tight">{otherName}</h4>
            <StatusChip tone={status.tone}>{status.label}</StatusChip>
          </div>
          <p className="text-xs text-gray-500 mt-0.5 truncate tabular-nums">{otherEmail}</p>
          <div className="flex items-center gap-3 flex-wrap mt-2 text-[11px] text-gray-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1 h-1 rounded-full bg-[#C5A059]"></span>
              <span className="font-medium">{isGrantor ? 'Sie geben Zugriff' : 'Sie erhalten Zugriff'}</span>
            </span>
            <span className="text-gray-300">·</span>
            <span>Lesender Zugriff</span>
            <span className="text-gray-300">·</span>
            <span className="tabular-nums">Erstellt {fmtDateTime(auth.created_at)}</span>
          </div>
        </div>

        <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
          {needsMySig ? (
            <ActionButton variant="gold" size="sm" onClick={(e) => { e.stopPropagation(); onSign(); }} data-testid={`sign-authorization-${auth.id}`}>
              Jetzt unterschreiben
            </ActionButton>
          ) : auth.status === 'active' ? (
            <ActionButton variant="danger" size="sm" onClick={(e) => { e.stopPropagation(); onRevoke(); }} data-testid={`revoke-authorization-${auth.id}`}>
              Widerrufen
            </ActionButton>
          ) : (
            <ActionButton variant="ghost" size="sm" icon={Icons.arrow}>
              Details
            </ActionButton>
          )}
        </div>
      </div>
      {/* Mobile action row */}
      <div className="sm:hidden px-4 pb-4 -mt-1">
        {needsMySig ? (
          <ActionButton variant="gold" size="sm" onClick={(e) => { e.stopPropagation(); onSign(); }} className="w-full">
            Jetzt unterschreiben
          </ActionButton>
        ) : auth.status === 'active' ? (
          <ActionButton variant="danger" size="sm" onClick={(e) => { e.stopPropagation(); onRevoke(); }} className="w-full">
            Widerrufen
          </ActionButton>
        ) : (
          <ActionButton variant="ghost" size="sm" className="w-full">
            Details ansehen
          </ActionButton>
        )}
      </div>
    </article>
  );
};

// ============================================================
// Create Modal
// ============================================================
const CreateAuthorizationModal = ({ currentUser, onClose, onCreated }) => {
  const [form, setForm] = useState({
    grantee_email: '',
    grantor_full_name: currentUser?.name || '',
    grantor_birthdate: '',
    grantor_address_street: '',
    grantor_address_zip: '',
    grantor_address_city: '',
    grantor_address_country: 'Deutschland',
    grantee_full_name: '',
    grantee_birthdate: '',
    grantee_address_street: '',
    grantee_address_zip: '',
    grantee_address_city: '',
    grantee_address_country: 'Deutschland',
  });
  const [submitting, setSubmitting] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.grantee_email.trim()) { toast.error('Bitte E-Mail der bevollmächtigten Person eintragen'); return; }
    if (!form.grantor_full_name.trim() || !form.grantee_full_name.trim()) {
      toast.error('Bitte beide Namen eintragen'); return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/account-authorizations`, form);
      toast.success('Vollmacht erstellt. Bitte jetzt unterschreiben.');
      onCreated(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Anlegen');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell onClose={onClose} testid="create-authorization-modal" title="Neue Kontovollmacht" subtitle="Lesezugriff für eine vertraute Person">
      <div className="space-y-6">
        <InfoBanner
          tone="info"
          icon={Icons.shield}
          title="Was bedeutet das?"
          description="Die bevollmächtigte Person sieht ausschließlich Ihre Investments, Auszahlungen und Dokumente. Sie kann nichts ändern, kündigen oder beantragen."
        />

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1.5">E-Mail der bevollmächtigten Person *</label>
          <input
            type="email" value={form.grantee_email} onChange={upd('grantee_email')}
            placeholder="email@beispiel.de"
            className="input-field tabular-nums"
            data-testid="grantee-email-input"
          />
          <p className="text-[11px] text-gray-500 mt-1">Die Person muss bereits einen ValuePlus-Account haben.</p>
        </div>

        <Fieldset legend="Ihre Daten (Vollmachtgeber/in)">
          <Input label="Vollständiger Name *" value={form.grantor_full_name} onChange={upd('grantor_full_name')} testid="grantor-name-input" />
          <Input label="Geburtsdatum" value={form.grantor_birthdate} onChange={upd('grantor_birthdate')} placeholder="TT.MM.JJJJ" testid="grantor-birthdate-input" />
          <Input label="Straße & Hausnummer" value={form.grantor_address_street} onChange={upd('grantor_address_street')} testid="grantor-street-input" colSpan />
          <Input label="PLZ" value={form.grantor_address_zip} onChange={upd('grantor_address_zip')} testid="grantor-zip-input" />
          <Input label="Ort" value={form.grantor_address_city} onChange={upd('grantor_address_city')} testid="grantor-city-input" />
          <Input label="Land" value={form.grantor_address_country} onChange={upd('grantor_address_country')} testid="grantor-country-input" />
        </Fieldset>

        <Fieldset legend="Bevollmächtigte Person">
          <Input label="Vollständiger Name *" value={form.grantee_full_name} onChange={upd('grantee_full_name')} testid="grantee-name-input" />
          <Input label="Geburtsdatum" value={form.grantee_birthdate} onChange={upd('grantee_birthdate')} placeholder="TT.MM.JJJJ" testid="grantee-birthdate-input" />
          <Input label="Straße & Hausnummer" value={form.grantee_address_street} onChange={upd('grantee_address_street')} testid="grantee-street-input" colSpan />
          <Input label="PLZ" value={form.grantee_address_zip} onChange={upd('grantee_address_zip')} testid="grantee-zip-input" />
          <Input label="Ort" value={form.grantee_address_city} onChange={upd('grantee_address_city')} testid="grantee-city-input" />
          <Input label="Land" value={form.grantee_address_country} onChange={upd('grantee_address_country')} testid="grantee-country-input" />
        </Fieldset>

        <div className="flex gap-3 pt-2 border-t border-gray-100">
          <ActionButton variant="secondary" size="md" onClick={onClose} disabled={submitting} className="flex-1">Abbrechen</ActionButton>
          <ActionButton variant="primary" size="md" onClick={submit} disabled={submitting} className="flex-1" data-testid="submit-authorization-button">
            {submitting ? 'Wird erstellt…' : 'Vollmacht anlegen & unterschreiben'}
          </ActionButton>
        </div>
      </div>
    </ModalShell>
  );
};

const Fieldset = ({ legend, children }) => (
  <fieldset className="rounded-xl border border-gray-100 bg-gray-50/50 p-4">
    <legend className="text-xs font-semibold text-[#0A0A0A] px-1.5 -ml-1.5">{legend}</legend>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">{children}</div>
  </fieldset>
);

const Input = ({ label, testid, colSpan, ...rest }) => (
  <div className={colSpan ? 'sm:col-span-2' : ''}>
    <label className="block text-[11px] font-medium text-gray-600 mb-1">{label}</label>
    <input className="input-field tabular-nums" data-testid={testid} {...rest} />
  </div>
);

// ============================================================
// Sign Modal
// ============================================================
const SignModal = ({ auth, onClose, onSign }) => (
  <ModalShell onClose={onClose} testid="sign-modal" title="Vollmacht unterschreiben" subtitle="Digitale Unterschrift" size="md">
    <div className="space-y-5">
      <div className="rounded-xl bg-gray-50 border border-gray-100 p-4 text-sm space-y-2">
        <div className="flex justify-between gap-3"><span className="text-gray-500">Vollmachtgeber/in</span><span className="font-medium text-right">{auth.grantor_full_name}</span></div>
        <div className="flex justify-between gap-3"><span className="text-gray-500">Bevollmächtigte/r</span><span className="font-medium text-right">{auth.grantee_full_name}</span></div>
        <div className="flex justify-between gap-3"><span className="text-gray-500">Umfang</span><span className="font-medium text-right">Lesezugriff</span></div>
      </div>
      <p className="text-xs text-gray-500 leading-relaxed">
        Mit Ihrer Unterschrift bestätigen Sie die Erteilung einer Kontovollmacht im oben genannten Umfang. Sie können den Zugriff jederzeit widerrufen.
      </p>
      <SignaturePad
        label="Bitte mit Maus oder Finger unterschreiben"
        confirmLabel="Unterschrift absenden"
        onConfirm={onSign}
        onCancel={onClose}
      />
    </div>
  </ModalShell>
);

// ============================================================
// Detail Modal
// ============================================================
const AuthorizationDetailModal = ({ auth, onClose, onRevoke, onSign }) => {
  const status = STATUS[auth.status] || STATUS.revoked;
  const canSign =
    (auth.role_for_me === 'grantor' && auth.status === 'pending_grantor_signature') ||
    (auth.role_for_me === 'grantee' && auth.status === 'pending_grantee_signature');
  const canRevoke = ['pending_grantor_signature', 'pending_grantee_signature', 'pending_admin_review', 'active'].includes(auth.status);

  return (
    <ModalShell onClose={onClose} testid="authorization-detail-modal" title={`Vollmacht #${auth.id}`} subtitle={<StatusChip tone={status.tone}>{status.label}</StatusChip>}>
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <PersonCard role="Vollmachtgeber/in" person={{
            name: auth.grantor_full_name, email: auth.grantor_email,
            birthdate: auth.grantor_birthdate, street: auth.grantor_address_street,
            zip: auth.grantor_address_zip, city: auth.grantor_address_city,
            country: auth.grantor_address_country, signed_at: auth.grantor_signed_at,
          }} />
          <PersonCard role="Bevollmächtigte/r" person={{
            name: auth.grantee_full_name, email: auth.grantee_email,
            birthdate: auth.grantee_birthdate, street: auth.grantee_address_street,
            zip: auth.grantee_address_zip, city: auth.grantee_address_city,
            country: auth.grantee_address_country, signed_at: auth.grantee_signed_at,
          }} />
        </div>

        {auth.admin_approved_at && (
          <div className="text-xs text-emerald-700 px-4 py-3 bg-emerald-50 rounded-lg border border-emerald-100">
            ✓ Aktiviert durch Value Plus am <span className="tabular-nums">{fmtDateTime(auth.admin_approved_at)}</span>
          </div>
        )}
        {auth.admin_rejected_at && (
          <div className="text-xs text-red-700 p-4 bg-red-50 rounded-lg border border-red-100">
            <p>Abgelehnt am <span className="tabular-nums">{fmtDateTime(auth.admin_rejected_at)}</span></p>
            {auth.admin_note && <p className="mt-1">Begründung: {auth.admin_note}</p>}
          </div>
        )}
        {auth.revoked_at && (
          <p className="text-xs text-gray-500 px-4 py-3 bg-gray-50 rounded-lg border border-gray-100">
            Widerrufen am <span className="tabular-nums">{fmtDateTime(auth.revoked_at)}</span>
          </p>
        )}

        <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-100">
          {auth.pdf_available && (
            <a
              href={`${API}/account-authorizations/${auth.id}/pdf`}
              target="_blank" rel="noopener noreferrer"
              data-testid="download-authorization-pdf"
            >
              <ActionButton variant="secondary" size="sm" icon={Icons.doc}>Vollmachts-PDF</ActionButton>
            </a>
          )}
          {canSign && (
            <ActionButton variant="gold" size="sm" onClick={onSign} data-testid="sign-from-detail-button">
              Jetzt unterschreiben
            </ActionButton>
          )}
          {canRevoke && (
            <ActionButton variant="danger" size="sm" onClick={onRevoke} icon={Icons.trash} data-testid="revoke-authorization-button">
              Widerrufen
            </ActionButton>
          )}
          <ActionButton variant="ghost" size="sm" className="ml-auto" onClick={onClose}>Schließen</ActionButton>
        </div>
      </div>
    </ModalShell>
  );
};

const PersonCard = ({ role, person }) => (
  <div className="rounded-xl border border-gray-100 bg-gray-50/40 p-4">
    <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">{role}</p>
    <div className="flex items-start gap-3">
      <Avatar name={person.name} size={40} />
      <div className="min-w-0">
        <p className="font-medium text-[#0A0A0A] truncate">{person.name || '—'}</p>
        <p className="text-xs text-gray-500 truncate">{person.email || '—'}</p>
      </div>
    </div>
    <dl className="mt-4 space-y-1.5 text-xs">
      {person.birthdate && <DataRow label="Geburtsdatum" value={person.birthdate} />}
      {person.street && <DataRow label="Adresse" value={<>{person.street}<br/>{person.zip} {person.city}{person.country ? `, ${person.country}` : ''}</>} />}
      <DataRow
        label="Unterschrift"
        value={person.signed_at ? <span className="text-emerald-700">{fmtDateTime(person.signed_at)}</span> : <span className="text-gray-400">Noch nicht erfolgt</span>}
      />
    </dl>
  </div>
);

const DataRow = ({ label, value }) => (
  <div className="flex justify-between gap-2">
    <dt className="text-gray-500 flex-shrink-0">{label}</dt>
    <dd className="text-right text-[#0A0A0A] tabular-nums">{value}</dd>
  </div>
);

// ============================================================
// Modal shell — shared chrome for all modals
// ============================================================
const ModalShell = ({ onClose, title, subtitle, children, testid, size = 'lg' }) => {
  const widths = { md: 'max-w-xl', lg: 'max-w-2xl', xl: 'max-w-4xl' };
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in" data-testid={testid}>
      <div className={`bg-white rounded-t-2xl sm:rounded-2xl ${widths[size]} w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl`}>
        <header className="px-5 sm:px-7 py-4 sm:py-5 border-b border-gray-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-heading text-lg sm:text-xl text-[#0A0A0A]">{title}</h3>
            {subtitle && (typeof subtitle === 'string'
              ? <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
              : <div className="mt-1.5">{subtitle}</div>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1 -m-1" aria-label="Schließen">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 sm:px-7 py-5 sm:py-6">
          {children}
        </div>
      </div>
    </div>
  );
};

export default CustomerAuthorizations;
